import React, { useEffect, useState } from "react";
import { Outlet, useParams } from "react-router-dom";

import { getBrandBySlugForAmbassadorOnboarding } from "@api/brands";
import { Brand } from "@types";
import Toaster from "@components/Toaster";
import LoadingSpinner from "@components/LoadingSpinner";
import TermsOfUse from "./fragments/TermsOfUse";
import ApplicationsClosedMessage from "./fragments/ApplicationsClosed";

export default function AmbassadorOnboardingLayout() {
  const { brand: brand_slug } = useParams<{ brand: string }>();

  const [isLoading, setLoading] = useState(true);

  const [brand, setBrand] = useState<Brand>(null);

  useEffect(() => {
    const loadAmbassadorOnboardingSettings = async () => {
      try {
        const settings =
          await getBrandBySlugForAmbassadorOnboarding(brand_slug);

        setLoading(false);

        setBrand(settings);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    if (!brand_slug) {
      return;
    }

    loadAmbassadorOnboardingSettings();
  }, [brand_slug]);

  const {
    background_photo,
    logo_photo,
    amp_description,
    slug,
    accepting_applications,
  } = brand || {};

  const BackgroundPhotoSection = ({ isOriginalLayout = false }) => (
    <div
      className={
        isOriginalLayout
          ? "h-[450px] sm:h-full relative w-screen sm:w-auto"
          : "h-full w-full relative"
      }
    >
      <div className="absolute h-full w-full left-[0px] right-[0px] bg-[#0c040454]" />
      {isOriginalLayout && (
        <div className="absolute top-[30px] sm:top-[25%] left-[20px] sm:left-[80px] w-[350px] sm:w-[450px]">
          <p className="mb-4 sm:mb-10 text-white text-2xl sm:text-4xl">
            {slug === "dick-s-2025"
              ? "Join the Varsity Team"
              : "Join the Ambassador Team"}
          </p>
          <p className="text-white">
            {amp_description}
            <br />
          </p>
        </div>
      )}
      <img
        src={background_photo?.original}
        className="h-full w-full object-cover"
      />
    </div>
  );

  if (isLoading) {
    return <LoadingSpinner className="w-full h-full mt-[20%]" />;
  }

  // Original Layout (for screens above 1200px)
  const OriginalLayout = () => (
    <div className="flex flex-row h-screen w-full">
      <div className="w-1/2 flex flex-col items-center justify-center h-full p-0 overflow-y-auto">
        <div className="max-h-full py-8">
          <a href="">
            <img
              src={logo_photo?.original}
              className="mb-[30px] w-[180px] mx-auto"
            />
          </a>
          {accepting_applications ? (
            <div className="w-full flex-1 c-lg:w-[440px]">
              <Outlet />
            </div>
          ) : (
            <ApplicationsClosedMessage />
          )}
          <TermsOfUse />
        </div>
      </div>
      <div className="w-1/2">
        <BackgroundPhotoSection isOriginalLayout={true} />
      </div>
    </div>
  );

  // New Layout (for screens 1200px and below)
  const NewLayout = () => (
    <div className="relative w-full min-h-screen text-white font-sofia_pro overflow-hidden">
      {/* Full screen background image */}
      <div className="absolute inset-0 z-0">
        <BackgroundPhotoSection />
      </div>

      {/* Content container with header text and form */}
      <div className="relative z-10 flex min-h-screen h-full flex-col items-center justify-center w-full">
        <div className="flex flex-col items-center py-[40px] max-w-screen-xl w-full px-4">
          {/* Form with blurred white background */}
          <div className="w-full sm:w-[500px] p-7 rounded-xl bg-white/80 backdrop-blur-md shadow-lg text-dark_night_sky">
            <a href="" className="mb-6">
              <img
                src={logo_photo?.original}
                className="mb-[30px] w-[180px] mx-auto"
              />
            </a>
            <div className="my-6 text-center">
              <p className="mb-4 text-black text-4xl">
                {slug === "dick-s-2025"
                  ? "Join the Varsity Team"
                  : "Join the Ambassador Team"}
              </p>
              <p className="text-black max-w-lg mx-auto mb-8">
                {amp_description}
              </p>
            </div>
            {accepting_applications ? (
              <div className="w-full">
                <Outlet />
              </div>
            ) : (
              <ApplicationsClosedMessage />
            )}
          </div>
          <TermsOfUse />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toaster />
      {/* Hide for large screens (above 1200px) */}
      <div className="hidden xl:block">
        <OriginalLayout />
      </div>

      {/* Show for small and medium screens (1200px and below) */}
      <div className="block xl:hidden">
        <NewLayout />
      </div>
    </>
  );
}
